import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ordenesTrabajoAPI, clientesAPI } from '../services/api';
import { useApp } from '../context/AppContext';

const ESTADOS = {
  recibida: { label: 'Recibida', color: 'info', icon: 'bi-inbox' },
  en_laboratorio: { label: 'En Lab.', color: 'warning', icon: 'bi-gear' },
  listo: { label: 'Listo', color: 'success', icon: 'bi-check-circle' },
  entregada: { label: 'Entregada', color: 'primary', icon: 'bi-bag-check' },
  cancelada: { label: 'Cancelada', color: 'danger', icon: 'bi-x-circle' }
};

const TIPOS = [
  { value: 'armazon_completo', label: 'Armazón Completo' },
  { value: 'solo_lentes', label: 'Solo Lentes' },
  { value: 'reparacion', label: 'Reparación' },
  { value: 'adaptacion_lc', label: 'Adaptación LC' },
  { value: 'otro', label: 'Otro' }
];

const PRIORIDADES = [
  { value: 'baja', label: 'Baja', color: 'secondary' },
  { value: 'normal', label: 'Normal', color: 'primary' },
  { value: 'alta', label: 'Alta', color: 'warning' },
  { value: 'urgente', label: 'Urgente', color: 'danger' }
];

const OrdenTrabajo = () => {
  const { formatCurrency } = useApp();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [buscar, setBuscar] = useState('');
  const [vista, setVista] = useState('tabla'); // tabla | kanban
  const [resumen, setResumen] = useState(null);

  // Modal crear/editar
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});

  // Modal detalle
  const [showDetalle, setShowDetalle] = useState(false);
  const [ordenDetalle, setOrdenDetalle] = useState(null);

  // Modal cambio estado
  const [showEstado, setShowEstado] = useState(false);
  const [estadoForm, setEstadoForm] = useState({ estado: '', notas: '' });
  const [ordenEstado, setOrdenEstado] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrdenes, setTotalOrdenes] = useState(0);

  // Búsqueda cliente
  const [clienteBuscar, setClienteBuscar] = useState('');
  const [clientesSugeridos, setClientesSugeridos] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);

  const cargarOrdenes = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 20, page };
      if (filtroEstado) params.estado = filtroEstado;
      if (buscar) params.buscar = buscar;
      const res = await ordenesTrabajoAPI.getAll(params);
      if (res.data.success) {
        setOrdenes(res.data.data);
        setTotalOrdenes(res.data.total || res.data.data.length);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (e) { toast.error('Error cargando órdenes'); }
    finally { setLoading(false); }
  }, [filtroEstado, buscar, page]);

  const cargarResumen = async () => {
    try {
      const res = await ordenesTrabajoAPI.getResumen();
      if (res.data.success) setResumen(res.data.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { cargarOrdenes(); cargarResumen(); }, [cargarOrdenes]);

  // ─── BUSCAR CLIENTE ───
  useEffect(() => {
    if (clienteBuscar.length < 2) { setClientesSugeridos([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await clientesAPI.getAll({ buscar: clienteBuscar, limit: 5 });
        if (res.data.success) setClientesSugeridos(res.data.data);
      } catch (e) { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [clienteBuscar]);

  const initialForm = {
    tipo: 'armazon_completo', armazon_propio: false, armazon_descripcion: '',
    rx_od_esfera: '', rx_od_cilindro: '', rx_od_eje: '', rx_od_adicion: '',
    rx_oi_esfera: '', rx_oi_cilindro: '', rx_oi_eje: '', rx_oi_adicion: '',
    rx_dp: '', rx_tipo_lente: '',
    tipo_lente: '', material_lente: '', tratamiento: '', color_lente: '',
    laboratorio: '', costo_laboratorio: '',
    prioridad: 'normal', fecha_promesa: '', notas: '', numero_orden: ''
  };

  const abrirCrear = () => {
    setEditando(null);
    setForm(initialForm);
    setClienteSeleccionado(null);
    setClienteBuscar('');
    setShowModal(true);
  };

  const abrirEditar = (orden) => {
    setEditando(orden);
    setForm({
      tipo: orden.tipo, armazon_propio: orden.armazon_propio,
      armazon_descripcion: orden.armazon_descripcion || '',
      rx_od_esfera: orden.rx_od_esfera || '', rx_od_cilindro: orden.rx_od_cilindro || '',
      rx_od_eje: orden.rx_od_eje || '', rx_od_adicion: orden.rx_od_adicion || '',
      rx_oi_esfera: orden.rx_oi_esfera || '', rx_oi_cilindro: orden.rx_oi_cilindro || '',
      rx_oi_eje: orden.rx_oi_eje || '', rx_oi_adicion: orden.rx_oi_adicion || '',
      rx_dp: orden.rx_dp || '', rx_tipo_lente: orden.rx_tipo_lente || '',
      tipo_lente: orden.tipo_lente || '', material_lente: orden.material_lente || '',
      tratamiento: orden.tratamiento || '', color_lente: orden.color_lente || '',
      laboratorio: orden.laboratorio || '', costo_laboratorio: orden.costo_laboratorio || '',
      prioridad: orden.prioridad, fecha_promesa: orden.fecha_promesa?.split('T')[0] || '',
      notas: orden.notas || ''
    });
    setClienteSeleccionado({ id: orden.cliente_id, nombres: orden.cliente_nombre });
    setShowModal(true);
  };

  const guardar = async () => {
    try {
      if (!clienteSeleccionado?.id && !editando) {
        return toast.warning('Seleccione un cliente');
      }
      const payload = { ...form, cliente_id: clienteSeleccionado?.id || editando?.cliente_id };
      if (editando) {
        await ordenesTrabajoAPI.update(editando.id, payload);
        toast.success('Orden actualizada');
      } else {
        const res = await ordenesTrabajoAPI.create(payload);
        const codigo = res?.data?.data?.codigo;
        toast.success(
          codigo
            ? <span>Orden creada: <b style={{ fontSize: '1.15rem' }}>{codigo}</b></span>
            : 'Orden creada exitosamente',
          { autoClose: 8000 }
        );
      }
      setShowModal(false);
      cargarOrdenes();
      cargarResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al guardar');
    }
  };

  const verDetalle = async (id) => {
    try {
      const res = await ordenesTrabajoAPI.getById(id);
      if (res.data.success) {
        setOrdenDetalle(res.data.data);
        setShowDetalle(true);
      }
    } catch (e) { toast.error('Error cargando detalle'); }
  };

  const abrirCambioEstado = (orden) => {
    setOrdenEstado(orden);
    setEstadoForm({ estado: '', notas: '' });
    setShowEstado(true);
  };

  const cambiarEstado = async () => {
    if (!estadoForm.estado) return toast.warning('Seleccione un estado');
    try {
      await ordenesTrabajoAPI.cambiarEstado(ordenEstado.id, estadoForm);
      toast.success('Estado actualizado');
      setShowEstado(false);
      cargarOrdenes();
      cargarResumen();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const getNextStates = (estado) => {
    const transitions = {
      recibida: ['en_laboratorio', 'cancelada'],
      en_laboratorio: ['listo', 'recibida', 'cancelada'],
      listo: ['entregada', 'en_laboratorio'],
      entregada: [],
      cancelada: ['recibida']
    };
    return transitions[estado] || [];
  };

  // ─── RENDERIZAR RX ───
  const RxDisplay = ({ label, esf, cil, eje, add }) => (
    <div className="d-flex gap-3">
      <strong>{label}:</strong>
      <span>Esf: {esf || '—'}</span>
      <span>Cil: {cil || '—'}</span>
      <span>Eje: {eje || '—'}</span>
      {add && <span>Add: {add}</span>}
    </div>
  );

  // ─── VISTA KANBAN ───
  const VistaKanban = () => {
    const estadosKanban = ['recibida', 'en_laboratorio', 'listo'];
    return (
      <div className="row g-3">
        {estadosKanban.map(estado => {
          const items = ordenes.filter(o => o.estado === estado);
          const est = ESTADOS[estado];
          return (
            <div key={estado} className="col-md-4">
              <div className="card">
                <div className={`card-header bg-${est.color} bg-opacity-10 text-${est.color}`}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0"><i className={`bi ${est.icon} me-2`}></i>{est.label}</h6>
                    <span className="badge bg-dark">{items.length}</span>
                  </div>
                </div>
                <div className="card-body p-2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {items.length === 0 ? (
                    <div className="text-center text-muted py-3"><small>Sin órdenes</small></div>
                  ) : items.map(o => (
                    <div key={o.id} className="card mb-2 border-start border-3"
                         style={{ borderColor: `var(--bs-${PRIORIDADES.find(p => p.value === o.prioridad)?.color || 'primary'}) !important`, cursor: 'pointer' }}
                         onClick={() => verDetalle(o.id)}>
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between">
                          <strong className="text-primary fw-bold" style={{ fontSize: '0.9rem' }}>{o.codigo}</strong>
                          <span className={`badge bg-${PRIORIDADES.find(p => p.value === o.prioridad)?.color}`} style={{ fontSize: '0.7rem' }}>
                            {o.prioridad}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.85rem' }}>{o.cliente_nombre}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {TIPOS.find(t => t.value === o.tipo)?.label}
                          {o.fecha_promesa && (
                            <span className={new Date(o.fecha_promesa) < new Date() ? 'text-danger ms-2' : ' ms-2'}>
                              <i className="bi bi-calendar me-1"></i>
                              {new Date(o.fecha_promesa).toLocaleDateString('es-EC')}
                            </span>
                          )}
                        </div>
                        <div className="mt-1">
                          {getNextStates(o.estado).slice(0, 2).map(ns => (
                            <button key={ns} className={`btn btn-sm btn-outline-${ESTADOS[ns].color} me-1`}
                                    style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                                    onClick={(e) => { e.stopPropagation(); abrirCambioEstado(o); }}>
                              <i className={`bi ${ESTADOS[ns].icon} me-1`}></i>{ESTADOS[ns].label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      {/* ─── RESUMEN ─── */}
      {resumen && (
        <div className="row g-2 mb-3">
          {Object.entries(ESTADOS).filter(([k]) => k !== 'cancelada').map(([key, val]) => {
            const item = resumen.por_estado?.find(e => e.estado === key);
            return (
              <div key={key} className="col">
                <div className={`card text-center border-${val.color}`} style={{ cursor: 'pointer' }}
                     onClick={() => { setPage(1); setFiltroEstado(filtroEstado === key ? '' : key); }}>
                  <div className={`card-body py-2 ${filtroEstado === key ? `bg-${val.color} bg-opacity-10` : ''}`}>
                    <h4 className={`mb-0 text-${val.color}`}>{item?.cantidad || 0}</h4>
                    <small className="text-muted">{val.label}</small>
                  </div>
                </div>
              </div>
            );
          })}
          {resumen.vencidas > 0 && (
            <div className="col">
              <div className="card text-center border-danger">
                <div className="card-body py-2">
                  <h4 className="mb-0 text-danger">{resumen.vencidas}</h4>
                  <small className="text-danger">Vencidas</small>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── CONTROLES ─── */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <div className="flex-grow-1">
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input type="text" className="form-control" placeholder="Buscar por código, cliente, CI..."
                   value={buscar} onChange={e => { setPage(1); setBuscar(e.target.value); }} />
          </div>
        </div>
        <div className="btn-group">
          <button className={`btn ${vista === 'tabla' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setVista('tabla')}><i className="bi bi-list-ul"></i></button>
          <button className={`btn ${vista === 'kanban' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setVista('kanban')}><i className="bi bi-kanban"></i></button>
        </div>
        <button className="btn btn-primary" onClick={abrirCrear}>
          <i className="bi bi-plus-lg me-1"></i>Nueva Orden
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : vista === 'kanban' ? (
        <VistaKanban />
      ) : (
        /* ─── VISTA TABLA ─── */
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Cliente</th>
                    <th>Tipo</th>
                    <th>Prioridad</th>
                    <th>Estado</th>
                    <th>F. Promesa</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ordenes.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-4 text-muted">No hay órdenes de trabajo</td></tr>
                  ) : ordenes.map(o => (
                    <tr key={o.id} className={o.fecha_promesa && new Date(o.fecha_promesa) < new Date() && !['entregada', 'cancelada'].includes(o.estado) ? 'table-danger' : ''}>
                      <td><strong className="text-primary fw-bold fs-6" style={{ cursor: 'pointer' }} onClick={() => verDetalle(o.id)}>{o.codigo}</strong>{o.numero_orden && <small className="text-muted d-block">Orden: {o.numero_orden}</small>}</td>
                      <td>
                        <div>{o.cliente_nombre}</div>
                        <small className="text-muted">{o.cliente_ci}</small>
                      </td>
                      <td>{TIPOS.find(t => t.value === o.tipo)?.label}</td>
                      <td>
                        <span className={`badge bg-${PRIORIDADES.find(p => p.value === o.prioridad)?.color}`}>
                          {o.prioridad}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${ESTADOS[o.estado]?.color}`}>
                          <i className={`bi ${ESTADOS[o.estado]?.icon} me-1`}></i>
                          {ESTADOS[o.estado]?.label}
                        </span>
                      </td>
                      <td>
                        {o.fecha_promesa ? (
                          <span className={new Date(o.fecha_promesa) < new Date() && !['entregada', 'cancelada'].includes(o.estado) ? 'text-danger fw-bold' : ''}>
                            {new Date(o.fecha_promesa).toLocaleDateString('es-EC')}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary" onClick={() => verDetalle(o.id)} title="Ver">
                            <i className="bi bi-eye"></i>
                          </button>
                          {!['entregada', 'cancelada'].includes(o.estado) && (
                            <>
                              <button className="btn btn-outline-secondary" onClick={() => abrirEditar(o)} title="Editar">
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-outline-success" onClick={() => abrirCambioEstado(o)} title="Cambiar Estado">
                                <i className="bi bi-arrow-right-circle"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {ordenes.length > 0 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <span className="text-muted">Mostrando {ordenes.length} de {totalOrdenes} orden(es)</span>
              {totalPages > 1 && (
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p => p - 1)}>Anterior</button>
                    </li>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pg;
                      if (totalPages <= 5) pg = i + 1;
                      else if (page <= 3) pg = i + 1;
                      else if (page >= totalPages - 2) pg = totalPages - 4 + i;
                      else pg = page - 2 + i;
                      return (
                        <li key={pg} className={`page-item ${page === pg ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(pg)}>{pg}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p => p + 1)}>Siguiente</button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══════ MODAL CREAR/EDITAR ═══════ */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-plus me-2"></i>
                  {editando ? 'Editar Orden' : 'Nueva Orden de Trabajo'}
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Cliente */}
                {!editando && (
                  <div className="mb-3">
                    <label className="form-label fw-bold"><i className="bi bi-person me-1"></i>Cliente *</label>
                    {clienteSeleccionado ? (
                      <div className="alert alert-info d-flex justify-content-between align-items-center py-2 mb-0">
                        <span><i className="bi bi-person-check me-2"></i><strong>{clienteSeleccionado.nombres} {clienteSeleccionado.apellidos || ''}</strong></span>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => { setClienteSeleccionado(null); setClienteBuscar(''); }}>
                          <i className="bi bi-x"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="position-relative">
                        <input type="text" className="form-control" placeholder="Buscar por nombre o CI..."
                               value={clienteBuscar} onChange={e => setClienteBuscar(e.target.value)} />
                        {clientesSugeridos.length > 0 && (
                          <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 1050, maxHeight: 200, overflowY: 'auto' }}>
                            {clientesSugeridos.map(c => (
                              <button key={c.id} className="list-group-item list-group-item-action"
                                      onClick={() => { setClienteSeleccionado(c); setClientesSugeridos([]); setClienteBuscar(''); }}>
                                <strong>{c.nombres} {c.apellidos}</strong>
                                <small className="text-muted ms-2">{c.identificacion}</small>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Tipo y Prioridad */}
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Tipo de Trabajo</label>
                    <select className="form-select" value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}>
                      {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Prioridad</label>
                    <select className="form-select" value={form.prioridad} onChange={e => setForm({ ...form, prioridad: e.target.value })}>
                      {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Fecha Promesa</label>
                    <input type="date" className="form-control" value={form.fecha_promesa}
                           onChange={e => setForm({ ...form, fecha_promesa: e.target.value })} />
                  </div>
                </div>

                {/* Armazón */}
                <div className="card mb-3">
                  <div className="card-header py-2"><h6 className="mb-0"><i className="bi bi-eyeglasses me-2"></i>Armazón</h6></div>
                  <div className="card-body py-2">
                    <div className="form-check mb-2">
                      <input type="checkbox" className="form-check-input" id="armazonPropio"
                             checked={form.armazon_propio} onChange={e => setForm({ ...form, armazon_propio: e.target.checked })} />
                      <label className="form-check-label" htmlFor="armazonPropio">Armazón propio del cliente</label>
                    </div>
                    <input type="text" className="form-control" placeholder="Descripción del armazón (marca, modelo, color...)"
                           value={form.armazon_descripcion} onChange={e => setForm({ ...form, armazon_descripcion: e.target.value })} />
                  </div>
                </div>

                {/* Prescripción */}
                <div className="card mb-3">
                  <div className="card-header py-2"><h6 className="mb-0"><i className="bi bi-eyedropper me-2"></i>Prescripción (Rx)</h6></div>
                  <div className="card-body py-2">
                    <div className="row g-2 mb-2">
                      <div className="col-2"><label className="form-label fw-bold">OD</label></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Esfera" value={form.rx_od_esfera} onChange={e => setForm({ ...form, rx_od_esfera: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Cilindro" value={form.rx_od_cilindro} onChange={e => setForm({ ...form, rx_od_cilindro: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Eje" value={form.rx_od_eje} onChange={e => setForm({ ...form, rx_od_eje: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Adición" value={form.rx_od_adicion} onChange={e => setForm({ ...form, rx_od_adicion: e.target.value })} /></div>
                    </div>
                    <div className="row g-2 mb-2">
                      <div className="col-2"><label className="form-label fw-bold">OI</label></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Esfera" value={form.rx_oi_esfera} onChange={e => setForm({ ...form, rx_oi_esfera: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Cilindro" value={form.rx_oi_cilindro} onChange={e => setForm({ ...form, rx_oi_cilindro: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Eje" value={form.rx_oi_eje} onChange={e => setForm({ ...form, rx_oi_eje: e.target.value })} /></div>
                      <div className="col"><input type="text" className="form-control form-control-sm" placeholder="Adición" value={form.rx_oi_adicion} onChange={e => setForm({ ...form, rx_oi_adicion: e.target.value })} /></div>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <input type="text" className="form-control form-control-sm" placeholder="DP (mm)" value={form.rx_dp} onChange={e => setForm({ ...form, rx_dp: e.target.value })} />
                      </div>
                      <div className="col-md-8">
                        <input type="text" className="form-control form-control-sm" placeholder="Tipo de lente prescrito" value={form.rx_tipo_lente} onChange={e => setForm({ ...form, rx_tipo_lente: e.target.value })} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Datos del lente */}
                <div className="card mb-3">
                  <div className="card-header py-2"><h6 className="mb-0"><i className="bi bi-disc me-2"></i>Datos del Lente</h6></div>
                  <div className="card-body py-2">
                    <div className="row g-2">
                      <div className="col-md-6"><input type="text" className="form-control" placeholder="Tipo de lente" value={form.tipo_lente} onChange={e => setForm({ ...form, tipo_lente: e.target.value })} /></div>
                      <div className="col-md-6"><input type="text" className="form-control" placeholder="Material" value={form.material_lente} onChange={e => setForm({ ...form, material_lente: e.target.value })} /></div>
                      <div className="col-md-8"><input type="text" className="form-control" placeholder="Tratamientos (antireflejo, fotocromático...)" value={form.tratamiento} onChange={e => setForm({ ...form, tratamiento: e.target.value })} /></div>
                      <div className="col-md-4"><input type="text" className="form-control" placeholder="Color" value={form.color_lente} onChange={e => setForm({ ...form, color_lente: e.target.value })} /></div>
                    </div>
                  </div>
                </div>

                {/* [N° orden física] Código de la orden de papel */}
                <div className="mb-3">
                  <label className="form-label"><i className="bi bi-hash me-1"></i>N° Orden física (opcional)</label>
                  <input type="text" className="form-control" placeholder="Código de la orden de papel" maxLength={50}
                         value={form.numero_orden || ''} onChange={e => setForm({ ...form, numero_orden: e.target.value })} />
                </div>

                {/* Laboratorio */}
                <div className="row g-3 mb-3">
                  <div className="col-md-8">
                    <label className="form-label">Laboratorio</label>
                    <input type="text" className="form-control" placeholder="Nombre del laboratorio"
                           value={form.laboratorio} onChange={e => setForm({ ...form, laboratorio: e.target.value })} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Costo Lab.</label>
                    <input type="number" className="form-control" placeholder="$0.00" step="0.01"
                           value={form.costo_laboratorio} onChange={e => setForm({ ...form, costo_laboratorio: e.target.value })} />
                  </div>
                </div>

                {/* Notas */}
                <div className="mb-3">
                  <label className="form-label">Notas</label>
                  <textarea className="form-control" rows="2" value={form.notas}
                            onChange={e => setForm({ ...form, notas: e.target.value })}></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={guardar}>
                  <i className="bi bi-check-lg me-1"></i>{editando ? 'Actualizar' : 'Crear Orden'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ MODAL DETALLE ═══════ */}
      {showDetalle && ordenDetalle && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-clipboard-data me-2"></i>
                  Orden <span className="fw-bold">{ordenDetalle.codigo}</span>
                  <span className={`badge bg-${ESTADOS[ordenDetalle.estado]?.color} ms-2`}>
                    {ESTADOS[ordenDetalle.estado]?.label}
                  </span>
                </h5>
                <button className="btn-close" onClick={() => setShowDetalle(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header py-2"><strong><i className="bi bi-person me-1"></i>Cliente</strong></div>
                      <div className="card-body py-2">
                        <div>{ordenDetalle.cliente_nombre}</div>
                        <small className="text-muted">CI: {ordenDetalle.cliente_ci}</small>
                        {ordenDetalle.cliente_telefono && <div><small><i className="bi bi-phone me-1"></i>{ordenDetalle.cliente_telefono}</small></div>}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header py-2"><strong><i className="bi bi-info-circle me-1"></i>Detalles</strong></div>
                      <div className="card-body py-2">
                        <div>Tipo: <strong>{TIPOS.find(t => t.value === ordenDetalle.tipo)?.label}</strong></div>
                        <div>Prioridad: <span className={`badge bg-${PRIORIDADES.find(p => p.value === ordenDetalle.prioridad)?.color}`}>{ordenDetalle.prioridad}</span></div>
                        {ordenDetalle.fecha_promesa && <div>Promesa: <strong>{new Date(ordenDetalle.fecha_promesa).toLocaleDateString('es-EC')}</strong></div>}
                        <div>Creado: {ordenDetalle.created_at ? new Date(ordenDetalle.created_at).toLocaleString('es-EC') : '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prescripción */}
                {(ordenDetalle.rx_od_esfera || ordenDetalle.rx_oi_esfera) && (
                  <div className="card mb-3">
                    <div className="card-header py-2"><strong><i className="bi bi-eyedropper me-1"></i>Prescripción</strong></div>
                    <div className="card-body py-2" style={{ fontSize: '0.9rem' }}>
                      <RxDisplay label="OD" esf={ordenDetalle.rx_od_esfera} cil={ordenDetalle.rx_od_cilindro} eje={ordenDetalle.rx_od_eje} add={ordenDetalle.rx_od_adicion} />
                      <RxDisplay label="OI" esf={ordenDetalle.rx_oi_esfera} cil={ordenDetalle.rx_oi_cilindro} eje={ordenDetalle.rx_oi_eje} add={ordenDetalle.rx_oi_adicion} />
                      {ordenDetalle.rx_dp && <div>DP: <strong>{ordenDetalle.rx_dp} mm</strong></div>}
                      {ordenDetalle.rx_tipo_lente && <div>Tipo Lente: <strong>{ordenDetalle.rx_tipo_lente}</strong></div>}
                    </div>
                  </div>
                )}

                {/* Lente + Lab */}
                <div className="row g-3 mb-3">
                  {(ordenDetalle.tipo_lente || ordenDetalle.material_lente) && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header py-2"><strong><i className="bi bi-disc me-1"></i>Lente</strong></div>
                        <div className="card-body py-2" style={{ fontSize: '0.9rem' }}>
                          {ordenDetalle.tipo_lente && <div>Tipo: {ordenDetalle.tipo_lente}</div>}
                          {ordenDetalle.material_lente && <div>Material: {ordenDetalle.material_lente}</div>}
                          {ordenDetalle.tratamiento && <div>Tratamiento: {ordenDetalle.tratamiento}</div>}
                          {ordenDetalle.color_lente && <div>Color: {ordenDetalle.color_lente}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                  {ordenDetalle.laboratorio && (
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header py-2"><strong><i className="bi bi-gear me-1"></i>Laboratorio</strong></div>
                        <div className="card-body py-2" style={{ fontSize: '0.9rem' }}>
                          <div>Lab: <strong>{ordenDetalle.laboratorio}</strong></div>
                          {ordenDetalle.costo_laboratorio > 0 && <div>Costo: <strong>{formatCurrency(ordenDetalle.costo_laboratorio)}</strong></div>}
                          {ordenDetalle.fecha_envio_lab && <div>Enviado: {new Date(ordenDetalle.fecha_envio_lab).toLocaleDateString('es-EC')}</div>}
                          {ordenDetalle.fecha_recepcion_lab && <div>Recibido: {new Date(ordenDetalle.fecha_recepcion_lab).toLocaleDateString('es-EC')}</div>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notas */}
                {ordenDetalle.notas && (
                  <div className="alert alert-light"><strong>Notas:</strong> {ordenDetalle.notas}</div>
                )}

                {/* Seguimiento */}
                {ordenDetalle.seguimiento?.length > 0 && (
                  <div className="card">
                    <div className="card-header py-2"><strong><i className="bi bi-clock-history me-1"></i>Seguimiento</strong></div>
                    <div className="card-body p-0">
                      <div className="list-group list-group-flush">
                        {ordenDetalle.seguimiento.map((s, i) => (
                          <div key={i} className="list-group-item py-2">
                            <div className="d-flex justify-content-between">
                              <div>
                                {s.estado_anterior && (
                                  <span className={`badge bg-${ESTADOS[s.estado_anterior]?.color} bg-opacity-50 me-1`} style={{ fontSize: '0.7rem' }}>
                                    {ESTADOS[s.estado_anterior]?.label}
                                  </span>
                                )}
                                <i className="bi bi-arrow-right mx-1"></i>
                                <span className={`badge bg-${ESTADOS[s.estado_nuevo]?.color}`} style={{ fontSize: '0.7rem' }}>
                                  {ESTADOS[s.estado_nuevo]?.label}
                                </span>
                              </div>
                              <small className="text-muted">{s.created_at ? new Date(s.created_at).toLocaleString('es-EC') : '—'}</small>
                            </div>
                            <small>{s.usuario} {s.notas ? `- ${s.notas}` : ''}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {!['entregada', 'cancelada'].includes(ordenDetalle.estado) && (
                  <button className="btn btn-success" onClick={() => { setShowDetalle(false); abrirCambioEstado(ordenDetalle); }}>
                    <i className="bi bi-arrow-right-circle me-1"></i>Cambiar Estado
                  </button>
                )}
                <button className="btn btn-secondary" onClick={() => setShowDetalle(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ MODAL CAMBIAR ESTADO ═══════ */}
      {showEstado && ordenEstado && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title"><i className="bi bi-arrow-right-circle me-2"></i>Cambiar Estado</h5>
                <button className="btn-close btn-close-white" onClick={() => setShowEstado(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p>Orden: <strong className="text-primary">{ordenEstado.codigo}</strong> - {ordenEstado.cliente_nombre}</p>
                  <p>Estado actual: <span className={`badge bg-${ESTADOS[ordenEstado.estado]?.color}`}>{ESTADOS[ordenEstado.estado]?.label}</span></p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Nuevo Estado</label>
                  <div className="d-flex gap-2 flex-wrap">
                    {getNextStates(ordenEstado.estado).map(ns => (
                      <button key={ns} className={`btn ${estadoForm.estado === ns ? `btn-${ESTADOS[ns].color}` : `btn-outline-${ESTADOS[ns].color}`}`}
                              onClick={() => setEstadoForm({ ...estadoForm, estado: ns })}>
                        <i className={`bi ${ESTADOS[ns].icon} me-1`}></i>{ESTADOS[ns].label}
                      </button>
                    ))}
                  </div>
                </div>
                {estadoForm.estado === 'en_laboratorio' && (
                  <div className="mb-3">
                    <label className="form-label">Fecha Envío a Laboratorio</label>
                    <input type="date" className="form-control" value={estadoForm.fecha_envio_lab || ''}
                           onChange={e => setEstadoForm({ ...estadoForm, fecha_envio_lab: e.target.value })} />
                  </div>
                )}
                {estadoForm.estado === 'listo' && (
                  <div className="mb-3">
                    <label className="form-label">Fecha Recepción de Laboratorio</label>
                    <input type="date" className="form-control" value={estadoForm.fecha_recepcion_lab || ''}
                           onChange={e => setEstadoForm({ ...estadoForm, fecha_recepcion_lab: e.target.value })} />
                  </div>
                )}
                <div className="mb-3">
                  <label className="form-label">Notas</label>
                  <textarea className="form-control" rows="2" value={estadoForm.notas}
                            onChange={e => setEstadoForm({ ...estadoForm, notas: e.target.value })}
                            placeholder="Observaciones del cambio..."></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEstado(false)}>Cancelar</button>
                <button className="btn btn-success" onClick={cambiarEstado} disabled={!estadoForm.estado}>
                  <i className="bi bi-check-lg me-1"></i>Confirmar Cambio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdenTrabajo;
